import { Input, Line } from 'components';
import { Alert, AlertType } from 'components/lds';
import { H3, M, S } from 'components/typography';
import { useChannelAdminContext } from 'hooks';
import useUserLocale from 'hooks/useUserLocale';
import {
  priceInputToComponents,
  setFinalPriceOnProduct,
} from 'lane-shared/domains/billingPayments/helpers';
import { useFinalPriceQuery } from 'lane-shared/domains/billingPayments/hooks';
import {
  AddItemType,
  AddProductToChargeDetails,
  Payer,
} from 'lane-shared/domains/billingPayments/types';
import { parseProductType, ProductType } from 'application-types';
import { getPricing } from 'lane-shared/domains/productCatalog/helpers';
import { convertToUUID } from 'uuid-encoding';
import {
  type SupportedPaymentCurrency,
  formatCurrency,
} from 'domains-billing-currency';
import { AddProductButtonGroup } from 'pages/portal/admin/channel/charges-invoices/add-product-to-charge/AddProductForm/AddProductButtonGroup';
import { AddProductCategoryAndProductSelection } from 'pages/portal/admin/channel/charges-invoices/add-product-to-charge/AddProductForm/AddProductCategoryAndProductSelection';
import {
  ModifierType,
  RateModifierInput,
} from 'pages/portal/admin/channel/products-services/products/RateModifierInput';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { isNumberTwoDecimalPlaces } from '../../../products-services/helpers';
import styles from '../addProductToChargeStyles.scss';
import { AddProductRadioGroup } from './AddProductRadioGroup';
import { EMPTY_TABLE_ROW_RPODUCT_DETAILS } from '../../constants';

export function AddProductModalForm({
  addProduct,
  editProduct,
  closeModal,
  formType,
  currency,
  payer,
  productToEdit,
  inclusivePricing = false,
}: {
  addProduct: Function;
  editProduct: Function;
  closeModal: Function;
  formType: AddItemType | undefined;
  currency: SupportedPaymentCurrency;
  payer: Payer;
  productToEdit?: AddProductToChargeDetails;
  inclusivePricing?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useUserLocale();
  const { channel } = useChannelAdminContext();
  const isBpAdminFeeChargeCodeEnabled = useBpAdminFeeChargeCodeEnabled();

  const [productDetails, setProductDetails] =
    useState<AddProductToChargeDetails>(() => {
      if (productToEdit) {
        return productToEdit;
      }

      return {
        ...EMPTY_TABLE_ROW_RPODUCT_DETAILS,
        tableRowId: uuid(),
        tableProductType: formType,
      };
    });

  const handleProductDetailsUpdate = (value: any, field: string) => {
    if (!field) {
      setProductDetails({ ...productDetails, ...value });

      return;
    }

    setProductDetails({ ...productDetails, [field]: value });
  };

  const resetProductDetails = () => {
    setProductDetails({
      ...EMPTY_TABLE_ROW_RPODUCT_DETAILS,
      tableRowId: uuid(),
      tableProductType: formType,
    });
  };

  const [productType, setProductType] = useState(() => {
    if (productToEdit) {
      return productToEdit.productCategoryType;
    }

    return ProductType.PRODUCT_TYPE_SERVICE_LABOUR;
  });

  const updateProductType = (value: string) => {
    const productTypeValue = parseProductType(value);

    setProductType(prev => {
      if (productTypeValue !== prev) {
        resetProductDetails();
      }

      return productTypeValue;
    });
  };

  useEffect(() => {
    const priceComponents = priceInputToComponents(productDetails);
    const { netPrice, formatPrice, listPrice, rate, grossTax, grossMarkup } =
      getPricing({
        currency,
        locale,
        priceComponents,
        inclusivePricing,
      });

    setProductDetails(prev => ({
      ...prev,
      total: formatPrice(netPrice * Number(productDetails.quantity)),
      netPrice: netPrice.toString(),
      rate: rate.toString(),
      sellingPrice: {
        grossTax: Number(grossTax),
        grossMarkup: Number(grossMarkup),
        listPrice: Number(listPrice),
        netPrice: Number(netPrice),
      },
    }));
  }, [
    productDetails?.markup?.value,
    productDetails?.tax?.value,
    productDetails.quantity,
    productDetails.rate,
    productDetails.netPrice,
    currency,
    locale,
  ]);

  const { finalPriceList } = useFinalPriceQuery({
    productIds: productDetails.productId
      ? [convertToUUID(productDetails.productId)]
      : [],
    payer,
    channel,
  });
  const finalPriceProductId = finalPriceList.length
    ? finalPriceList[0]?.productId
    : undefined;

  useEffect(() => {
    if (
      finalPriceProductId &&
      productDetails.productId &&
      finalPriceList[0].exceptionId &&
      finalPriceProductId === convertToUUID(productDetails.productId)
    ) {
      const finalPrice = finalPriceList[0].finalPrice;

      setProductDetails(prev => {
        const newProductDetails = setFinalPriceOnProduct(finalPrice, prev);
        const updatedSellingPrice = finalPriceList[0].sellingPrice || undefined;

        return {
          ...newProductDetails,
          sellingPrice: updatedSellingPrice,
          netPrice:
            updatedSellingPrice?.netPrice?.toString() ??
            newProductDetails.netPrice,
        };
      });
    }
  }, [channel?._id, finalPriceProductId, productDetails.productId]);

  const resetAndCloseModal = () => {
    resetProductDetails();
    closeModal();
  };

  const onAddProductClick = () => {
    if (productToEdit) {
      editProduct(productDetails);
      resetAndCloseModal();

      return;
    }

    addProduct(productDetails);
    resetAndCloseModal();
  };

  const onCancelClick = () => {
    if (productToEdit) {
      editProduct(undefined);
    }

    resetAndCloseModal();
  };

  const rateInputError = () => {
    if (!isNumberTwoDecimalPlaces(parseFloat(productDetails.rate))) {
      return [t('abp.productsServices.createProduct.max2Decimal')];
    }

    return null;
  };

  const netPriceInputError = () => {
    if (!isNumberTwoDecimalPlaces(parseFloat(productDetails.netPrice))) {
      return [t('abp.productsServices.createProduct.max2Decimal')];
    }

    return null;
  };

  return (
    <div className={styles.addProductModal}>
      <H3 className={styles.modalHeader} mb={4} bold>
        {t('abp.charges.addProduct.modal.header')}
      </H3>
      <div className={styles.modalFormContent}>
        <AddProductRadioGroup
          productType={productType}
          updateProductType={updateProductType}
        />
        <AddProductCategoryAndProductSelection
          formType={formType}
          productDetails={productDetails}
          productType={productType}
          handleProductDetailsUpdate={handleProductDetailsUpdate}
          currency={currency}
        />
        {productDetails.originalPrice && (
          <Alert type={AlertType.info} fullWidth>
            <S>{t('abp.charges.addProduct.modal.exceptionInfo')}</S>
          </Alert>
        )}
        <div className={styles.rateInputs}>
          {inclusivePricing ? (
            <Input
              className={styles.inputFields}
              label={t('abp.productsServices.createProduct.totalPrice.label')}
              fixedLabel
              isRequired
              value={productDetails.netPrice}
              onChange={(value: string) => {
                handleProductDetailsUpdate(value, 'netPrice');
              }}
              testId="productNetPriceInput"
              error={netPriceInputError()}
              disabled={!!productDetails.originalPrice}
            />
          ) : (
            <Input
              className={styles.inputFields}
              label={t('abp.productsServices.createProduct.rate.label')}
              fixedLabel
              isRequired
              value={productDetails.rate}
              onChange={(value: string) => {
                handleProductDetailsUpdate(value, 'rate');
              }}
              testId="productRateInput"
              error={rateInputError()}
              disabled={!!productDetails.originalPrice}
            />
          )}

          <RateModifierInput
            modifierType={productDetails.markup?.type}
            modifierValue={productDetails.markup?.value}
            type={ModifierType.MARKUP}
            updateDetails={handleProductDetailsUpdate}
            disabled={!!productDetails.originalPrice}
          />
          {isBpAdminFeeChargeCodeEnabled && (
            <RateModifierInput
              modifierType={productDetails.adminFee?.type}
              modifierValue={productDetails.adminFee?.value}
              type={ModifierType.ADMIN_FEE}
              updateDetails={handleProductDetailsUpdate}
              disabled={!!productDetails.originalPrice}
            />
          )}
          <RateModifierInput
            modifierType={productDetails.tax?.type}
            modifierValue={productDetails.tax?.value}
            type={ModifierType.TAX}
            updateDetails={handleProductDetailsUpdate}
            disabled={!!productDetails.originalPrice}
          />
        </div>

        <Input
          className={styles.quantityInput}
          label="Quantity"
          fixedLabel
          isRequired
          value={productDetails.quantity}
          onChange={(value: string) =>
            handleProductDetailsUpdate(value, 'quantity')
          }
          testId="productQuantityInput"
        />
        <div className={styles.totalSection}>
          <M bold>{t('Total')}</M>
          <M mb={2} data-test="totalCostText">
            {formatCurrency(currency)(Number(productDetails.total))}
          </M>
        </div>
      </div>

      <Line className={styles.buttonGroupLine} />

      <AddProductButtonGroup
        productDetails={productDetails}
        formType={formType}
        addProduct={onAddProductClick}
        onCancel={onCancelClick}
      />
    </div>
  );
}
